import { Container, Sprite, type DestroyOptions, type Texture } from "pixi.js";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import { POTS, type PotKind } from "../rules/symbols";
import { POT_SMOKE_TUNING } from "../tuning/featureTuning";
import { ART_SCALE, POT_SMOKE_LAYOUT, POTS_LAYOUT } from "./layout";

/**
 * The artist's pot smoke (`pot_smoke.riv`, played as its Spine conversion): its `triggered` boolean sends a plume up out
 * of the pot (about 0.4 s), then it billows on a loop; off, it dissipates (about 0.3 s) and draws nothing. Its `color` a
 * number per pot (the plume's size with it).
 */
export const POT_SMOKE_RIVE = {
  triggered: "triggered",
  color: "color",
  colors: { life: 1, multi: 2, grow: 3 } satisfies Record<PotKind, number>,
} as const;

/** What the smoke asks of its art (`SpineArtboard`: the Rive file's conversion, drawn into a texture of its own). */
export interface SmokeArt {
  readonly texture: Texture;
  resize(scale: number): void;
  setNumber(name: string, value: number): void;
  setBoolean(name: string, value: boolean): void;
  advance(deltaSeconds: number): boolean;
  render(deltaSeconds: number): void;
  dispose(): void;
}

interface Plume {
  art: SmokeArt;
  sprite: Sprite;
  /** Engine seconds it is still drawn after going off (0 while off: idle, hidden). */
  fading: number;
}

/**
 * The activated pots' smoke (the artist's handoff): while a pot's booster plays, a glowing plume in its colour billows
 * out of its opening — rising as the pot erupts into its booster, dissipating as the booster is spent or the bonus ends.
 * One artboard per pot (the file loaded once), drawn on the UI's runner while its plume is on and as it dissipates, at
 * the tuning's frame rate and resolution: never work a round waits for. The scene puts this layer over the pots and
 * under the jackpot plaques and the logo, so the plumes rise behind them. A pot set on before the smoke is in plumes as
 * it arrives.
 */
export class PotSmoke extends Container {
  private readonly ambientRunner: PresentationTaskRunner;
  private readonly log: Log;
  private plumes: Readonly<Record<PotKind, Plume>> | null = null;
  private readonly active: Record<PotKind, boolean> = { life: false, multi: false, grow: false };
  private drawing = false;
  /** Device pixels per design unit where the smoke plays (its canvases' size follows it). */
  private pixelsPerUnit = 1;

  public constructor(input: { ambientRunner: PresentationTaskRunner; log: Log }) {
    super({ label: "PotSmoke" });
    this.ambientRunner = input.ambientRunner;
    this.log = input.log;
  }

  /** The artist's smoke, loaded: an artboard per pot, each in its pot's colour on its opening. */
  public setArt(arts: Readonly<Record<PotKind, SmokeArt>>): void {
    if (this.destroyed || this.plumes) {
      for (const kind of POTS) arts[kind].dispose();
      return;
    }
    const { artboard, base } = POT_SMOKE_LAYOUT;
    const build = (kind: PotKind): Plume => {
      const art = arts[kind];
      art.setNumber(POT_SMOKE_RIVE.color, POT_SMOKE_RIVE.colors[kind]);
      art.resize(this.canvasScale());
      // Its base on the opening, at the mock-up's scale.
      const sprite = new Sprite({ texture: art.texture, anchor: { x: base.x / artboard.width, y: base.y / artboard.height }, label: `Smoke:${kind}` });
      sprite.setSize(artboard.width * ART_SCALE, artboard.height * ART_SCALE);
      sprite.position.set(POTS_LAYOUT.opening[kind].x, POTS_LAYOUT.opening[kind].y);
      sprite.visible = false;
      this.addChild(sprite);
      return { art, sprite, fading: 0 };
    };
    this.plumes = { life: build("life"), multi: build("multi"), grow: build("grow") };
    this.log("[PotSmoke] the smoke is in");
    for (const kind of POTS) if (this.active[kind]) this.apply(kind, this.plumes[kind]);
  }

  /** Whether the smoke is in, and which pots plume (the dev hook's read). */
  public get state(): { loaded: boolean } & Record<PotKind, boolean> {
    return { loaded: this.plumes !== null, ...this.active };
  }

  /** Device pixels per design unit where the smoke plays: its canvases are drawn at the tuning's share of that. */
  public setPixelsPerUnit(pixelsPerUnit: number): void {
    if (!(pixelsPerUnit > 0) || pixelsPerUnit === this.pixelsPerUnit) return;
    this.pixelsPerUnit = pixelsPerUnit;
    if (this.plumes) for (const kind of POTS) this.plumes[kind].art.resize(this.canvasScale());
  }

  /** `pot`'s plume on (it rises, then billows) or off (it dissipates); the same again changes nothing. */
  public set(pot: PotKind, on: boolean): void {
    if (this.active[pot] === on) return;
    this.active[pot] = on;
    const plume = this.plumes?.[pot];
    if (plume && !this.destroyed) this.apply(pot, plume);
  }

  public override destroy(options?: DestroyOptions): void {
    const plumes = this.plumes;
    this.plumes = null;
    if (plumes) for (const kind of POTS) plumes[kind].art.dispose();
    super.destroy(options);
  }

  /** A plume told its state, shown and drawn: while on, and for its dissipating after. */
  private apply(pot: PotKind, plume: Plume): void {
    plume.art.setBoolean(POT_SMOKE_RIVE.triggered, this.active[pot]);
    plume.fading = this.active[pot] ? 0 : POT_SMOKE_TUNING.offSeconds;
    if (this.active[pot]) plume.sprite.visible = true;
    if (plume.sprite.visible) plume.art.render(0);
    this.draw();
  }

  /** Whether a pot's plume is drawn: on, or dissipating. */
  private drawn(kind: PotKind): boolean {
    const plume = this.plumes?.[kind];
    return plume !== undefined && plume.sprite.visible && (this.active[kind] || plume.fading > 0);
  }

  /** The plumes drawn at their own pace while one is on or dissipating; each hidden once it has dissipated. */
  private draw(): void {
    if (this.drawing) return;
    this.drawing = true;
    let last = 0;
    void this.ambientRunner
      .tickInScope(
        "",
        (elapsed) => {
          const delta = elapsed - last;
          last = elapsed;
          for (const kind of POTS) {
            const plume = this.plumes?.[kind];
            if (!plume || !this.drawn(kind)) continue;
            plume.art.advance(delta);
            if (this.active[kind]) continue;
            plume.fading = Math.max(0, plume.fading - delta);
            if (plume.fading === 0) plume.sprite.visible = false;
          }
        },
        () => this.destroyed || !this.plumes || POTS.every((kind) => !this.drawn(kind)),
      )
      .then(() => {
        this.drawing = false;
        // A plume went on as the last one dissipated.
        if (!this.destroyed && this.plumes && POTS.some((kind) => this.drawn(kind))) this.draw();
      });
  }

  /** Canvas pixels per artboard pixel: the art's scale on the screen, at the tuning's share. */
  private canvasScale(): number {
    return ART_SCALE * this.pixelsPerUnit * POT_SMOKE_TUNING.resolution;
  }
}
